import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { css, styled } from "styled-components";

import { ExpandButton } from "common/components";
import { navbarComplementHeight } from "common/constants/navbarHeight";
import { breakpoints } from "common/styles";

const Container = styled.div`
  display: flex;
  height: ${navbarComplementHeight};
  overflow-x: clip;
`;

const Centre = styled.div`
  display: none;
  flex: 3;

  @media ${breakpoints.large} {
    display: flex;
  }
`;

const panelStyle = css`
  position: relative;
  transition:
    flex 0.3s,
    max-width 0.3s;
  width: 0;
`;

const Left = styled.div<{ show: boolean }>`
  display: none;
  flex: ${({ show }) => (show ? 2 : 0)};
  max-width: 450px;
  z-index: 1;

  ${panelStyle};

  @media ${breakpoints.enormous} {
    display: flex;
  }
`;

const Right = styled.div<{ show: boolean; showLeftPanel: boolean }>`
  z-index: 1;
  flex: ${({ show }) => (show ? 2 : 0)};

  ${panelStyle};

  @media ${breakpoints.medium} {
    display: flex;
  }

  @media ${breakpoints.large} {
    max-width: ${({ showLeftPanel }) => (showLeftPanel ? 650 : 800)}px;
  }
`;

const PanelLeftContainer = styled.div`
  display: flex;
  width: 100%;
  overflow: auto;
`;

const PanelRightContainer = styled.div`
  width: 100%;
  overflow: scroll;
`;

type VisibilityChange = { right: boolean } | { left: boolean };

type PanelContainerProps = PropsWithChildren & {
  onPanelVisibilityToggle: (visibilityChange: VisibilityChange) => void;
  panelLeft: ReactNode;
  panelRight: ReactNode;
};

export const PanelContainer = ({ children, onPanelVisibilityToggle, panelLeft, panelRight }: PanelContainerProps) => {
  const [showLeftPanel, setShowLeftPanel] = useState(!!panelLeft);
  const [showRightPanel, setShowRightPanel] = useState(!!panelRight);

  const toggleLeftPanel = useCallback(
    (isOpen: boolean) => {
      setShowLeftPanel(isOpen);
      if (onPanelVisibilityToggle) {
        onPanelVisibilityToggle({ left: isOpen });
      }
    },
    [onPanelVisibilityToggle],
  );

  const toggleRightPanel = useCallback(
    (isOpen: boolean) => {
      setShowRightPanel(isOpen);
      if (onPanelVisibilityToggle) {
        onPanelVisibilityToggle({ right: isOpen });
      }
    },
    [onPanelVisibilityToggle],
  );

  const isPanelLeftAvailable = !!panelLeft;
  useEffect(() => {
    toggleLeftPanel(isPanelLeftAvailable);
  }, [isPanelLeftAvailable, toggleLeftPanel]);

  const isPanelRightAvailable = !!panelRight;
  useEffect(() => {
    toggleRightPanel(isPanelRightAvailable);
  }, [isPanelRightAvailable, toggleRightPanel]);

  return (
    <Container>
      {panelLeft && (
        <Left show={showLeftPanel}>
          <ExpandButton isOpen={showLeftPanel} onClick={() => toggleLeftPanel(!showLeftPanel)} />
          <PanelLeftContainer inert={!showLeftPanel}>{panelLeft}</PanelLeftContainer>
        </Left>
      )}
      <Centre>{children}</Centre>
      {panelRight && (
        <Right show={showRightPanel} showLeftPanel={showLeftPanel}>
          <ExpandButton isOpen={showRightPanel} onClick={() => toggleRightPanel(!showRightPanel)} isRightPanel />
          <PanelRightContainer inert={!showRightPanel}>{panelRight}</PanelRightContainer>
        </Right>
      )}
    </Container>
  );
};
